/**
 * Triage Store
 * 
 * Zustand store for triage management with assessment actions and selectors.
 * Part of Workstream 3: Triage System
 * 
 * IMPORTANT: Triage outcomes actually affect item state:
 * - pursue → item moves to 'triaged' detailed state and becomes available for continued item work
 * - defer → item is deferred, removed from active queues
 * - reject → item is rejected, removed from active queues
 * - needs_clarification → stays in triage queue until resolved
 * - redirect → records the triage decision and reroutes the item through canonical queue routing
 */

import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import type {
  TriageAssessment,
  TriageOutcome,
  TriageQueueItem,
  TriageStats,
  TriageBucket,
  BatchTriageInput,
} from '../../types/prioritization/triage';
import { createEmptyQuickChecks, validateTriageAssessment } from '../../types/prioritization/triage';
import { useItemsStore, recordItemFieldChange } from './itemsStore';
import { useUserStore } from './userStore';
import {
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
  isSupabaseAvailable,
} from '../../lib/supabaseSync';
import { checkPolicyForItem } from '../../lib/authorization/storeEnforcement';

const TABLE_NAME = 'triage_assessments';

/**
 * Record one triage decision through `record_triage_outcome` (migration 1046):
 * the item moves (exit status, stage, queue on a redirect) and its
 * triage_assessments row is written or updated in one transaction, or neither.
 * It was two client writes — the item first, then the row — and a refused
 * second write left the item moved with no decision behind it. The iPhone
 * calls the same function (apps/ios DataService DB.triage).
 */
async function recordTriageOutcome(itemId: string, assessment: TriageAssessment): Promise<void> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    throw new Error('Database unavailable. Triage decisions cannot be saved.');
  }

  const { data, error } = await supabase.rpc('record_triage_outcome', {
    p_item_id: itemId,
    p_outcome: assessment.outcome,
    p_rationale: assessment.rationale ?? null,
    p_redirect_target: assessment.redirectTarget ?? null,
    p_clarification_request: assessment.clarificationRequest ?? null,
    p_clarification_requested_from: assessment.clarificationRequestedFrom ?? null,
    p_quick_checks: assessment.quickChecks ?? {},
    p_bucket_id: assessment.bucketId ?? null,
  });
  if (error) {
    throw new Error(error.message);
  }
  const result = (data ?? {}) as { from_exit_status?: string | null; to_exit_status?: string | null };

  // Triage saves straight to the item, so it records its own history (2026-10-15).
  recordItemFieldChange(itemId, 'triage', null, assessment.outcome);
  const fromState = result.from_exit_status ?? null;
  if (fromState !== (result.to_exit_status ?? null)) {
    recordItemFieldChange(itemId, 'exit_status', fromState, result.to_exit_status ?? null);
  }

  await useItemsStore.getState().loadItemById(itemId);
}

interface TriageState {
  // Data
  assessments: Record<string, TriageAssessment>;
  buckets: Record<string, TriageBucket>;
  
  // Draft state (for in-progress assessments)
  draftAssessments: Record<string, Partial<TriageAssessment>>;
  
  // Loading state
  isLoading: boolean;
  error: string | null;
  
  // Supabase sync
  fetchFromSupabase: () => Promise<void>;
  
  // Actions
  submitTriageAssessment: (itemId: string, assessment: Omit<TriageAssessment, 'id' | 'triagedAt' | 'triagedBy'>) => Promise<TriageAssessment>;
  updateTriageAssessment: (id: string, updates: Partial<TriageAssessment>) => void;
  bulkTriage: (input: BatchTriageInput) => Promise<TriageAssessment[]>;
  requestClarification: (itemId: string, question: string, requestedFrom?: string) => TriageAssessment;
  
  // Draft actions
  saveDraft: (itemId: string, draft: Partial<TriageAssessment>) => void;
  clearDraft: (itemId: string) => void;
  getDraft: (itemId: string) => Partial<TriageAssessment> | undefined;
  
  // Bucket actions
  createBucket: (bucket: Omit<TriageBucket, 'id' | 'itemCount' | 'avgTriageTimeMinutes'>) => TriageBucket;
  updateBucket: (id: string, updates: Partial<TriageBucket>) => void;
  deleteBucket: (id: string) => void;
  
  // Selectors
  getTriageAssessment: (itemId: string) => TriageAssessment | undefined;
  getAssessmentsForItem: (itemId: string) => TriageAssessment[];
  getTriageQueue: (itemIds: string[]) => TriageQueueItem[];
  getTriageStats: () => TriageStats;
  getAssessmentsByOutcome: (outcome: TriageOutcome) => TriageAssessment[];
  getAssessmentsByBucket: (bucketId: string) => TriageAssessment[];
  getBuckets: () => TriageBucket[];
  
  // Utility
  reset: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

const initialState = {
  assessments: {},
  buckets: {},
  draftAssessments: {},
  isLoading: false,
  error: null,
};

export const useTriageStore = create<TriageState>()(
  (set, get) => ({
    ...initialState,
    
    // Supabase sync
    fetchFromSupabase: async () => {
      if (!isSupabaseAvailable()) {
        console.log('[TriageStore] Supabase not available');
        return;
      }
      
      set({ isLoading: true, error: null });
      
      try {
        const workspaceId = getCurrentWorkspaceId();
        if (!workspaceId) {
          set({ assessments: {}, isLoading: false, error: null });
          return;
        }
        const supabase = getSafeSupabaseClient();
        if (!supabase) return;
        
        const { data, error } = await supabase
          .from(TABLE_NAME)
          .select('*')
          .eq('workspace_id', workspaceId)
          .order('triaged_at', { ascending: false });
        
        if (error) {
          console.error('[TriageStore] Fetch error:', error);
          set({ isLoading: false, error: error.message });
          return;
        }
        
        if (data && data.length > 0) {
          const assessmentsMap: Record<string, TriageAssessment> = {};
          for (const row of data) {
            const assessment: TriageAssessment = {
              id: row.id,
              itemId: row.item_id,
              quickChecks: row.quick_checks || {},
              outcome: row.outcome,
              rationale: row.rationale,
              triagedBy: row.triaged_by,
              triagedAt: new Date(row.triaged_at),
              bucketId: row.bucket_id,
              redirectTarget: row.redirect_target,
              clarificationRequest: row.clarification_request,
              clarificationRequestedFrom: row.clarification_requested_from,
              workspaceId: row.workspace_id,
            };
            assessmentsMap[assessment.itemId] = assessment;
          }
          set({ assessments: assessmentsMap, isLoading: false, error: null });
          console.log(`[TriageStore] Loaded ${data.length} triage assessments from Supabase`);
        } else {
          console.log('[TriageStore] No triage assessments found in Supabase (table may be empty or not exist)');
          set({ assessments: {}, isLoading: false, error: null });
        }
      } catch (err) {
        console.error('[TriageStore] Fetch exception:', err);
        set({ isLoading: false, error: err instanceof Error ? err.message : 'Unknown error' });
      }
    },
    
    // Submit triage assessment
    submitTriageAssessment: async (itemId: string, assessment: Omit<TriageAssessment, 'id' | 'triagedAt' | 'triagedBy'>): Promise<TriageAssessment> => {
        const id = uuidv4();
        const now = new Date();
        
        // Get the current user ID from user store
        const userId = useUserStore.getState().userId;
        
        const fullAssessment: TriageAssessment = {
          ...assessment,
          id,
          itemId,
          triagedAt: now,
          triagedBy: userId,
        };
        
        const workspaceId = getCurrentWorkspaceId();
        if (!workspaceId) {
          throw new Error('No workspace selected. Triage cannot be saved.');
        }
        if (!isSupabaseAvailable()) {
          throw new Error('Database unavailable. Triage decisions cannot be saved.');
        }

        // Validate
        const errors = validateTriageAssessment(fullAssessment);
        if (errors.length > 0) {
          throw new Error(errors.join(' '));
        }
        fullAssessment.workspaceId = workspaceId;

        let item = useItemsStore.getState().items.find((entry) => entry.id === itemId);
        if (!item) {
          await useItemsStore.getState().loadItemById(itemId);
          item = useItemsStore.getState().items.find((entry) => entry.id === itemId);
        }
        if (!item) {
          throw new Error(`Item ${itemId} not found while preparing triage.`);
        }

        const policyDecision = checkPolicyForItem('items.triage', itemId, item as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: {
                store: 'triage',
                action: 'submitTriageAssessment',
                message: `Cannot triage item: ${reasonCode}`,
                decision: policyDecision,
              },
            }));
          }
          throw new Error(`Cannot triage item: ${reasonCode}`);
        }

        // One act: the item moves and the decision is recorded, or neither (1046).
        await recordTriageOutcome(itemId, fullAssessment);

        set(state => ({
          assessments: { ...state.assessments, [itemId]: fullAssessment },
          draftAssessments: (() => {
            const { [itemId]: _, ...rest } = state.draftAssessments;
            return rest;
          })(),
        }));
        
        return fullAssessment;
      },
      
      // Update assessment
      updateTriageAssessment: (id: string, updates: Partial<TriageAssessment>) => {
        set(state => {
          // Find by id
          const entry = Object.entries(state.assessments).find(([_, a]) => a.id === id);
          if (!entry) return state;
          
          const [itemId, assessment] = entry;
          const updated = { ...assessment, ...updates };
          
          return {
            assessments: { ...state.assessments, [itemId]: updated },
          };
        });
      },
      
      // Bulk triage
      bulkTriage: async (input: BatchTriageInput): Promise<TriageAssessment[]> => {
        const results: TriageAssessment[] = [];

        for (const itemId of input.itemIds) {
          const assessment = await get().submitTriageAssessment(itemId, {
            quickChecks: createEmptyQuickChecks(), // Default - caller should have validated
            outcome: input.outcome,
            rationale: input.rationale,
            bucketId: input.bucketId,
          });
          results.push(assessment);
        }

        return results;
      },
      
      // Request clarification
      requestClarification: (itemId: string, question: string, requestedFrom?: string): TriageAssessment => {
        const id = uuidv4();
        const now = new Date();
        const userId = useUserStore.getState().userId;
        
        const assessment: TriageAssessment = {
          id,
          itemId,
          triagedBy: userId,
          triagedAt: now,
          quickChecks: createEmptyQuickChecks(),
          outcome: 'needs_clarification',
          clarificationRequest: question,
          clarificationRequestedFrom: requestedFrom,
        };
        
        set(state => ({
          assessments: { ...state.assessments, [itemId]: assessment },
        }));
        
        return assessment;
      },
      
      // Save draft
      saveDraft: (itemId: string, draft: Partial<TriageAssessment>) => {
        set(state => ({
          draftAssessments: { ...state.draftAssessments, [itemId]: draft },
        }));
      },
      
      // Clear draft
      clearDraft: (itemId: string) => {
        set(state => {
          const { [itemId]: _, ...rest } = state.draftAssessments;
          return { draftAssessments: rest };
        });
      },
      
      // Get draft
      getDraft: (itemId: string): Partial<TriageAssessment> | undefined => {
        return get().draftAssessments[itemId];
      },
      
      // Create bucket
      createBucket: (bucket: Omit<TriageBucket, 'id' | 'itemCount' | 'avgTriageTimeMinutes'>): TriageBucket => {
        const id = uuidv4();
        
        const fullBucket: TriageBucket = {
          ...bucket,
          id,
          itemCount: 0,
          avgTriageTimeMinutes: 0,
        };
        
        set(state => ({
          buckets: { ...state.buckets, [id]: fullBucket },
        }));
        
        return fullBucket;
      },
      
      // Update bucket
      updateBucket: (id: string, updates: Partial<TriageBucket>) => {
        set(state => {
          const existing = state.buckets[id];
          if (!existing) return state;
          
          return {
            buckets: { ...state.buckets, [id]: { ...existing, ...updates } },
          };
        });
      },
      
      // Delete bucket
      deleteBucket: (id: string) => {
        set(state => {
          const { [id]: _, ...rest } = state.buckets;
          return { buckets: rest };
        });
      },
      
      // Get assessment for item
      getTriageAssessment: (itemId: string): TriageAssessment | undefined => {
        return get().assessments[itemId];
      },
      
      // Get assessments for item (array for compatibility with integration tests / surfaces)
      getAssessmentsForItem: (itemId: string): TriageAssessment[] => {
        const a = get().assessments[itemId];
        return a ? [a] : [];
      },
      
      // Get triage queue
      getTriageQueue: (itemIds: string[]): TriageQueueItem[] => {
        const assessments = get().assessments;
        const now = new Date();
        
        // Items without assessment are in the queue
        return itemIds
          .filter(id => !assessments[id])
          .map(id => ({
            id,
            title: '', // Would need item data
            submittedBy: '',
            submittedAt: now,
            daysInQueue: 0,
            similarItems: [],
            hasUrgencySignal: false,
            hasExternalSource: false,
            hasStakeholderMention: false,
          }));
      },
      
      // Get stats
      getTriageStats: (): TriageStats => {
        const assessments = Object.values(get().assessments);
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Recent assessments
        const recentAssessments = assessments.filter(a => a.triagedAt >= thirtyDaysAgo);
        
        // By outcome
        const byOutcome: Record<TriageOutcome, number> = {
          pursue: 0,
          defer: 0,
          reject: 0,
          needs_clarification: 0,
          redirect: 0,
        };
        
        for (const a of recentAssessments) {
          byOutcome[a.outcome]++;
        }
        
        // Throughput
        const triagedToday = assessments.filter(a => a.triagedAt >= today).length;
        const triagedThisWeek = assessments.filter(a => a.triagedAt >= sevenDaysAgo).length;
        const triagedThisMonth = recentAssessments.length;
        
        // Rates
        const clarificationRequestRate = recentAssessments.length > 0
          ? byOutcome.needs_clarification / recentAssessments.length
          : 0;
        const redirectRate = recentAssessments.length > 0
          ? byOutcome.redirect / recentAssessments.length
          : 0;
        
        // By triager
        const byTriagerMap: Record<string, { count: number; totalTime: number }> = {};
        for (const a of recentAssessments) {
          if (!byTriagerMap[a.triagedBy]) {
            byTriagerMap[a.triagedBy] = { count: 0, totalTime: 0 };
          }
          byTriagerMap[a.triagedBy].count++;
        }
        
        const byTriager = Object.entries(byTriagerMap).map(([userId, data]) => ({
          userId,
          count: data.count,
          avgTimeMinutes: 5, // Placeholder - would need timing data
        }));
        
        return {
          totalInQueue: 0, // Would need item data
          averageDaysInQueue: 0,
          oldestItemDays: 0,
          byOutcome,
          triagedToday,
          triagedThisWeek,
          triagedThisMonth,
          clarificationRequestRate,
          redirectRate,
          byTriager,
        };
      },
      
      // Get by outcome
      getAssessmentsByOutcome: (outcome: TriageOutcome): TriageAssessment[] => {
        return Object.values(get().assessments).filter(a => a.outcome === outcome);
      },
      
      // Get by bucket
      getAssessmentsByBucket: (bucketId: string): TriageAssessment[] => {
        return Object.values(get().assessments).filter(a => a.bucketId === bucketId);
      },
      
      // Get all buckets
      getBuckets: (): TriageBucket[] => {
        return Object.values(get().buckets);
      },
      
      // Reset
      reset: () => set(initialState),
      
      // Loading
      setLoading: (loading: boolean) => set({ isLoading: loading }),
      setError: (error: string | null) => set({ error }),
    })
);

// Selector hooks
export const useTriageAssessment = (itemId: string) =>
  useTriageStore(state => state.getTriageAssessment(itemId));

export const useTriageStats = () =>
  useTriageStore(state => state.getTriageStats());

export const useTriageBuckets = () =>
  useTriageStore(state => state.getBuckets());

export const useTriageDraft = (itemId: string) =>
  useTriageStore(state => state.getDraft(itemId));
